/** Shared between the Cloudflare Worker and the browser bundle. */

export const MADEIRA_TZ = 'Atlantic/Madeira'

/** Which upstream booking system a club lives on. */
export type SourceId =
  | 'simplifica' // simplifica.madeira.gov.pt - Madeira regional government portal
  | 'tieplayer' // clubs.tiesports.com (tieplayer.com redirects here)
  | 'playtomic' // playtomic.com
  | 'field' // getfield.app - Firebase/Firestore backed
  | 'matchpoint' // TPC MatchPoint white-label (*.matchpoint.com.es)
  | 'manual' // no machine-readable availability; phone or members-only site

export type Surface = 'indoor' | 'outdoor' | 'covered'

export interface Court {
  /** The court's id on its own platform - the join key for availability. */
  id: string
  name: string
  /** Omitted where the platform does not publish it; never guessed. */
  surface?: Surface
}

/** The identifiers an adapter needs to address a club on its platform. */
export type PlatformRef =
  | { kind: 'simplifica'; serviceId: number; idProcess: number }
  | { kind: 'playtomic'; tenantId: string }
  | { kind: 'tiesports'; slug: string }
  | { kind: 'field'; clubId: string }
  | { kind: 'matchpoint'; host: string; idCuadro: number }
  | { kind: 'manual' }

/**
 * Simplifica publishes tariffs as free-text HTML rather than per-slot, so the
 * few clubs on it carry their price rule here instead. Euros per 60 minutes,
 * VAT included.
 */
export interface PriceRule {
  weekday: number
  evening: number
  weekend: number
  /** Madeira local HH:mm from which the evening rate applies. */
  eveningFrom: string
}

/** Coarse geography, used to group the club filter into something scannable. */
export type Region = 'Funchal' | 'West' | 'East' | 'North' | 'Porto Santo'

export const REGION_ORDER: Region[] = ['Funchal', 'West', 'East', 'North', 'Porto Santo']

export interface Club {
  id: string
  name: string
  /** Fits inside a map pin label and a calendar chip. */
  shortName: string
  municipality: string
  region: Region
  lat: number
  lon: number
  courts: Court[]
  source: SourceId
  ref: PlatformRef
  pricing?: PriceRule
  /**
   * How far ahead this club opens bookings. Past it the upstreams return an
   * empty 200, which would read as "fully booked" - so we simply don't ask.
   */
  horizonDays?: number
  /** Where a person completes the reservation. Deep-linked from every slot. */
  bookingUrl: string
  /** Tailwind-compatible hex, used for the map pin and the hover card accent. */
  color: string
  /** False when availability is static/unknown and we can only say "call them". */
  live: boolean
  /**
   * False when the platform will not honour a date in the booking URL for this
   * club, so the link stays on the plain club page rather than promising a day
   * the page cannot show.
   */
  deepLink?: boolean
  phone?: string
  note?: string
  /** Portuguese version of `note`, shown when the site is in Portuguese. */
  notePt?: string
}

/** The match lengths padel is actually booked in. */
export const DURATIONS = [60, 90, 120] as const
export type Duration = (typeof DURATIONS)[number]

/** One bookable length from a given start, with what it costs. */
export interface SlotOption {
  minutes: number
  /** Euros for the whole booking, or null when the upstream hides prices. */
  price: number | null
}

/**
 * One free start time on one court, already normalised into Madeira wall-clock
 * time by the Worker so the browser never reasons about WET/WEST transitions.
 *
 * A start is kept once, carrying every length bookable from it - that is what
 * lets the UI answer "show me where I can get 90 minutes".
 */
export interface Slot {
  clubId: string
  courtId: string
  courtName: string
  /** YYYY-MM-DD in Atlantic/Madeira. */
  date: string
  /** HH:mm in Atlantic/Madeira. */
  start: string
  /** Absolute instant, so "is this in the past" is unambiguous. */
  startUtc: string
  /** Bookable lengths from this start, ascending. Never empty. */
  options: SlotOption[]
  currency: string
  /** Deep link to this club's booking page. */
  bookingUrl: string
}

export function optionFor(slot: Slot, minutes: number): SlotOption | undefined {
  return slot.options.find((o) => o.minutes === minutes)
}

/** Per-source health, surfaced in the UI so a silent failure is never mistaken for "fully booked". */
export interface SourceStatus {
  source: SourceId
  clubIds: string[]
  ok: boolean
  cached: boolean
  error?: string
}

/**
 * How far each club's data actually reaches. Clubs open bookings on a rolling
 * window - Simplifica publishes only ~6 days - and a day beyond that window
 * renders exactly like a fully-booked one unless we say otherwise.
 */
export interface ClubCoverage {
  clubId: string
  /** Last Madeira date (YYYY-MM-DD) this club has real data for. */
  through: string
}

export interface AvailabilityResponse {
  /** Echo of the requested range, YYYY-MM-DD inclusive. */
  from: string
  to: string
  slots: Slot[]
  sources: SourceStatus[]
  coverage: ClubCoverage[]
  /** ISO instant the payload was assembled. */
  fetchedAt: string
}
